import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';

const REVIEW_PREVIEW_LINES = 5;
const READ_MORE_LABEL = 'Читать весь отзыв';

const trimToWordBoundary = (text, length) => {
  const shortened = text.slice(0, length).trimEnd();
  const lastSpace = shortened.lastIndexOf(' ');

  if (lastSpace > Math.floor(length * 0.6)) {
    return shortened.slice(0, lastSpace).trimEnd();
  }

  return shortened;
};

const TestimonialText = ({ text, isExpanded, onExpand }) => {
  const visibleRef = useRef(null);
  const measureRef = useRef(null);
  const [previewText, setPreviewText] = useState(text);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const visibleNode = visibleRef.current;
    const measureNode = measureRef.current;

    if (!visibleNode || !measureNode) {
      return undefined;
    }

    const renderMeasureContent = (candidate, withButton) => {
      measureNode.innerHTML = '';

      const textNode = document.createElement('span');
      textNode.textContent = withButton ? `"${candidate}..." ` : `"${candidate}"`;
      measureNode.appendChild(textNode);

      if (withButton) {
        const buttonNode = document.createElement('span');
        buttonNode.className = 'font-medium whitespace-nowrap';
        buttonNode.textContent = READ_MORE_LABEL;
        measureNode.appendChild(buttonNode);
      }
    };

    const recomputePreview = () => {
      const width = visibleNode.clientWidth;

      if (!width) {
        return;
      }

      measureNode.style.width = `${width}px`;

      const lineHeight = parseFloat(window.getComputedStyle(visibleNode).lineHeight);
      const maxHeight = lineHeight * REVIEW_PREVIEW_LINES + 1;

      renderMeasureContent(text, false);
      if (measureNode.offsetHeight <= maxHeight) {
        setPreviewText(text);
        setIsOverflowing(false);
        return;
      }

      let low = 0;
      let high = text.length;
      let bestFit = '';

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = trimToWordBoundary(text, mid);

        renderMeasureContent(candidate, true);

        if (measureNode.offsetHeight <= maxHeight) {
          bestFit = candidate;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      setPreviewText(bestFit || trimToWordBoundary(text, Math.floor(text.length * 0.7)));
      setIsOverflowing(true);
    };

    recomputePreview();

    const resizeObserver = new ResizeObserver(recomputePreview);
    resizeObserver.observe(visibleNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [text]);

  return (
    <div className="relative mb-8">
      <p
        ref={visibleRef}
        className="text-white text-xl md:text-2xl leading-relaxed font-light"
      >
        {isExpanded || !isOverflowing ? (
          `"${text}"`
        ) : (
          <>
            {`"${previewText}..." `}
            <button
              type="button"
              onClick={onExpand}
              className="font-medium text-sand-100 underline underline-offset-4 hover:text-white transition-colors whitespace-nowrap"
            >
              {READ_MORE_LABEL}
            </button>
          </>
        )}
      </p>

      <p
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 text-white text-xl md:text-2xl leading-relaxed font-light opacity-0"
      />
    </div>
  );
};

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isPaused || isExpanded) return undefined;
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [isExpanded, isPaused]);

  useEffect(() => {
    setIsExpanded(false);
  }, [currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      className="relative overflow-hidden bg-gradient-to-br from-teal-700 via-teal-800 to-teal-900 py-16 md:py-24"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Decorative Circles */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-teal-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
          <p className="text-sand-200 font-medium tracking-[0.3em] uppercase text-xs mb-2">
            Отзывы
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
            Что говорят клиенты
          </h2>
          <p className="text-sand-100 text-lg">
            Реальные истории людей, которым я помогла обрести гармонию
          </p>
          <p className="text-sand-200/90 text-sm mt-3">
            Отзывы клиентов (с согласия, данные частично изменены для конфиденциальности)
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl bg-white/10 p-6 backdrop-blur-sm md:p-12">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-10">
              <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
                <Quote className="text-stone-900" size={24} />
              </div>
            </div>

            {/* Testimonial Content */}
            <div className="pt-4">
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star key={i} size={20} className="text-amber-400 fill-amber-400" />
                ))}
              </div>

              {/* Text */}
              <TestimonialText
                text={testimonials[currentIndex].text}
                isExpanded={isExpanded}
                onExpand={() => setIsExpanded(true)}
              />

              {/* Author */}
              <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white text-xl font-serif">
                    {testimonials[currentIndex].name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-lg">
                      {testimonials[currentIndex].name}
                    </p>
                    <p className="break-words text-sm text-sand-200">
                      {testimonials[currentIndex].role} • {testimonials[currentIndex].location}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-2 self-start md:self-auto">
                  <button
                    onClick={prevSlide}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="text-white" size={24} />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="text-white" size={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="mt-8 hidden justify-center gap-2 sm:flex">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-amber-400 w-8'
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
          <div className="mt-6 flex justify-center sm:hidden">
            <div className="rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium text-sand-100">
              {currentIndex + 1} / {testimonials.length}
            </div>
          </div>
        </div>

        {/* Compact Preview Strip */}
        <div className="mt-10 flex flex-wrap justify-center gap-2 text-sand-200 text-xs">
          {Array.from(new Set(testimonials.map((t) => t.location))).map((location) => {
            const firstIndex = testimonials.findIndex((t) => t.location === location);
            const isActive = firstIndex === currentIndex;
            return (
              <button
                key={location}
                onClick={() => setCurrentIndex(firstIndex)}
                className={`rounded-full border px-3 py-1 transition-colors ${
                  isActive
                    ? 'border-amber-300 text-white'
                    : 'border-white/20 hover:border-white/50 hover:text-white'
                }`}
              >
                {location}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
